import {inject, Injectable} from '@angular/core';
import {defaultIfEmpty, filter, firstValueFrom, from, map, Observable, of, switchMap, tap, throwError} from "rxjs";
import {Analysis, SpareTyreResult} from "../../core/models/analysis";
import {AnalysisStage} from "../../core/models/analysis-stage";
import {CustomError} from "../../core/models/error";
import {AnalysisStatusEnum} from "../../core/models/analysis-status";
import {ReferenceApiService} from "../../core/api/reference-api/reference-api.service";
import {UploadQueueService} from "./upload-queue.service";
import {PictureStoreService} from "./picture-store.service";
import {canResumeAnalysis, SyncAction} from "../../core/models/analysis-sync";
import {AnalysisSyncService} from "./analysis-sync.service";
import {Product} from "../../core/models/product";
import {AppStateService} from "../../core/state/app-state.service";
import {AnalysisCreationService} from "./analysis-creation.service";
import {PhotoshootService} from "../../features/shared/photoshoot/services/photoshoot.service";
import {PostAnalysisResponseDto} from "../../core/dto/post-analysis-response-dto";
import {Vehicle} from "../../core/models/vehicle";
import {ExternalApiService} from "../../core/api/external-api/external-api.service";
import {FinishAnalysisRequestDto} from "../../core/dto/finish-analysis-request-dto";
import {FinishAnalysisResponseDto} from "../../core/dto/finish-analysis-response-dto";
import {AnalysisTimingService} from "./analysis-timing.service";

@Injectable({
  providedIn: 'root'
})
export class AnalysisService {

  protected referenceApi = inject(ReferenceApiService);
  protected externalApi = inject(ExternalApiService);
  protected uploadQueue = inject(UploadQueueService);
  protected pictureStore = inject(PictureStoreService);
  protected analysisSync = inject(AnalysisSyncService);
  protected appStateService = inject(AppStateService);
  protected analysisCreation = inject(AnalysisCreationService);
  protected photoshootService = inject(PhotoshootService);
  protected analysisTimingService = inject(AnalysisTimingService);

  getOrCreateAnalysis(): Observable<Analysis> {
    const analysis = this.appStateService.analysis();
    if (analysis?.id) {
      return of(analysis);
    }
    return from(this.createAnalysisAndVehicle()).pipe(
      map(() => this.appStateService.analysis()!)
    );
  }

  abortAnalysis() {
    const analysisId = this.appStateService.analysis()?.id;

    if (!analysisId) {
      return throwError(() => new Error(CustomError.NO_ANALYSIS));
    }

    // TODO(TAP-5508): no /v1/web-app endpoint to abort an analysis yet, still uses the reference API.
    return this.referenceApi.updateAnalysis(analysisId, AnalysisStatusEnum.ABORTED).pipe(
      switchMap(() => {
        if (this.appStateService.product() === Product.COLLECT || !canResumeAnalysis(this.appStateService.vehicle())) {
          return from(this.appStateService.clear()).pipe(map(() => null));
        }
        return from(this.appStateService.clearLocalPicturesAndAnalysis()).pipe(
          switchMap(() => from(this.createAnalysisAndVehicle()))
        );
      })
    );
  }

  // One common point to finish the analysis and abstract the timings
  finishAnalysis(dto: FinishAnalysisRequestDto = {}): Observable<FinishAnalysisResponseDto> {
    const analysisId = this.appStateService.analysis()?.id;
    if (!analysisId) {
      return throwError(() => new Error(CustomError.NO_ANALYSIS));
    }
    return this.externalApi.finishAnalysis(analysisId, {
      ...dto,
      ...this.analysisTimingService.getTimings(analysisId)
    });
  }

  async createAnalysisAndVehicle() {
    if (!this.appStateService.company()?.key) {
      return Promise.reject(new Error(CustomError.NO_SUCH_COMPANY));
    }
    const previousSpareTyre = this.appStateService.analysis()?.spareTyreResult;
    const analysisResponse = await firstValueFrom(this.analysisCreation.createOrResume$(this.appStateService.snapshot));
    const analysis = this.handleAnalysisCreation(analysisResponse);
    const {action, confirmedSteps} = await this.analysisSync.sync({
      analysis,
      vehicle: this.appStateService.vehicle(),
      scenario: this.appStateService.scenario(),
      product: this.appStateService.product()
    });
    if (action === SyncAction.REUSE) {
      await firstValueFrom(this.createSpareTyre(analysis, previousSpareTyre));
    }
    this.photoshootService.applyStoredPictures(await this.pictureStore.all(), confirmedSteps);
    return analysisResponse;
  }

  private createSpareTyre(analysis: Analysis, spareTyre?: SpareTyreResult) {
    if (!analysis.id || !spareTyre?.spareTyreResult) {
      return of(undefined);
    }
    return this.referenceApi.createSpareTyreAnalysis$(analysis.id).pipe(
      map((response) => response.id),
      filter((vehicleAnalysisId) => !!vehicleAnalysisId),
      switchMap((vehicleAnalysisId) => this.referenceApi.updateSpareTyreAnalysis$(vehicleAnalysisId, spareTyre.spareTyreResult!).pipe(
        map(() => vehicleAnalysisId))
      ),
      switchMap((vehicleAnalysisId) => this.referenceApi.updateVehicleAnalysis$(vehicleAnalysisId, AnalysisStatusEnum.UPLOADED).pipe(
        map(() => vehicleAnalysisId))
      ),
      tap((vehicleAnalysisId) => this.appStateService.patch({
        analysis: {
          ...this.appStateService.analysis(),
          spareTyreResult: {
            vehicleAnalysisId,
            spareTyreResult: spareTyre.spareTyreResult,
            stage: AnalysisStage.TERMINAL
          }
        } as Analysis
      })),
      defaultIfEmpty(undefined)
    );
  }

  private handleAnalysisCreation(analysisResponse: PostAnalysisResponseDto) {
    const analysis = Analysis.create(analysisResponse);
    const previousId = this.appStateService.analysis()?.id;
    if (previousId && previousId !== analysis.id) {
      this.photoshootService.clearPictures();
    }
    this.appStateService.patch({analysis, vehicle: this.identifiedVehicle(analysisResponse.vehicle)});
    return analysis;
  }

  private identifiedVehicle(response: PostAnalysisResponseDto['vehicle']): Vehicle {
    const current = this.appStateService.vehicle();
    const identified = Vehicle.create(response.id, response.license, response.licenseCountry);
    return {...current, id: identified.id, license: identified.license ?? current?.license} as Vehicle;
  }
}
